import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Cloud, Network, Headset, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Home = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Network className="w-12 h-12" />,
      name: "Network Management",
      description: "24/7 monitoring and management of your network infrastructure",
      image: "https://images.unsplash.com/photo-1680992045563-2ab442f3feee?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      name: "Cybersecurity",
      description: "Advanced threat protection and security solutions",
      image: "https://images.unsplash.com/photo-1768839721176-2fa91fdce725?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: <Cloud className="w-12 h-12" />,
      name: "Cloud Solutions",
      description: "Scalable cloud infrastructure and migration services",
      image: "https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    {
      icon: <Headset className="w-12 h-12" />,
      name: "IT Support",
      description: "Expert technical support for your business operations",
      image: "https://images.unsplash.com/photo-1736858405164-4f61f5583aa0?crop=entropy&cs=srgb&fm=jpg&q=85"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-background" />
        </div>
        
        {/* Scanlines Effect */}
        <div className="absolute inset-0 scanlines z-10" />
        
        {/* Content */}
        <div className="relative z-20 container mx-auto px-6 md:px-12 py-32 md:py-48">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="text-xs uppercase tracking-widest text-primary font-mono mb-6" data-testid="hero-kicker">
              ELITE MANAGED SERVICES
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight mb-6" data-testid="hero-title">
              NEXT<span className="text-primary">/</span>GEN<br />
              NETWORK SOLUTIONS
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mb-12 leading-relaxed" data-testid="hero-description">
              Secure, scalable, and always-on IT infrastructure for modern businesses.
              Your network. Our mission.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="text-base font-bold uppercase tracking-wider hover:glow-green-sm"
                onClick={() => navigate('/login')}
                data-testid="hero-client-portal-btn"
              >
                Client Portal
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="secondary"
                className="text-base"
                onClick={() => navigate('/services')}
                data-testid="hero-explore-services-btn"
              >
                Explore Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 relative" data-testid="services-section">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-black uppercase mb-4" data-testid="services-title">
              OUR SERVICES
            </h2>
            <div className="w-20 h-1 bg-primary" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-card border border-border rounded-sm overflow-hidden hover:border-primary/50 transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-primary mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{service.name}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-card border-y border-border" data-testid="cta-section">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-black uppercase mb-6" data-testid="cta-title">
              READY TO SECURE YOUR NETWORK?
            </h2>
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
              Access your account to manage services, view invoices, and get support.
            </p>
            <Button 
              size="lg" 
              className="text-base font-bold uppercase tracking-wider hover:glow-green"
              onClick={() => navigate('/login')}
              data-testid="cta-get-started-btn"
            >
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
